import { useField } from 'formik';

import { KubesoloVersions } from '@api/types.gen';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';

import { useKubesoloVersions } from './queries/useKubesoloVersions';
import { versionToInstall } from './versionToInstall';

interface Props {
  /** Machines that can't reach the internet install the bundled release */
  offline: boolean;
}

export function KubesoloVersionField({ offline }: Props) {
  const [field, meta, helpers] = useField<string>('kubesoloVersion');
  const versionsQuery = useKubesoloVersions();
  const versions = versionsQuery.data;

  return (
    <FormControl
      label="KubeSolo version"
      inputId="kubesolo-version"
      errors={meta.error}
      isLoading={versionsQuery.isLoading}
      loadingText="Loading the KubeSolo versions..."
    >
      {versions && (
        <>
          <PortainerSelect
            inputId="kubesolo-version"
            name={field.name}
            options={toOptions(versions)}
            value={versionToInstall(
              { kubesoloVersion: field.value, offline },
              versions.bundled
            )}
            onChange={(value) => helpers.setValue(value ?? '')}
            disabled={offline}
            data-cy="kubesolo-version-select"
          />
          <VersionsNote versions={versions} offline={offline} />
        </>
      )}
    </FormControl>
  );
}

function VersionsNote({
  versions,
  offline,
}: {
  versions: KubesoloVersions;
  offline: boolean;
}) {
  if (offline) {
    return (
      <span className="small text-muted">
        Machines that can&apos;t reach the internet install the KubeSolo version
        bundled with Portainer.
      </span>
    );
  }

  if (versions.githubError) {
    return (
      <TextTip color="orange">
        <span>
          Portainer couldn&apos;t list the KubeSolo releases on GitHub, so only
          the version bundled with Portainer is offered.
          <br />
          {versions.githubError}
        </span>
      </TextTip>
    );
  }

  return null;
}

function toOptions(versions: KubesoloVersions) {
  return versions.versions.map((version) => ({
    value: version,
    label: version === versions.bundled ? `${version} (bundled)` : version,
  }));
}
